import { useCallback } from "react";
import { findStringInArray } from "../../../utils/formGenUtils/viewModelMethods";
import FormInput, { FormProps } from "../input/FormInput";
import { Box, Input } from "./checkboxGroup.style";
import FormFieldGroup from "../formFieldGenerator/FormFieldGroup";

interface CheckBoxGroupProps
  extends Omit<FormProps, "name" | "onChange" | "value"> {
  options: any[];
  groupName: string;
  isExclusive?: boolean;
  value?: any;
  handleChange?: (fieldName: string) => any;
  shouldAskConfirmation?: boolean;
  setTempValue?: (value: any) => void;
  setShowConfirmationPrompt?: (value: boolean) => void;
  depField?: string;
  childFields?: {
    [id: string]: any;
  };
  getError?: any;
}

const CheckBoxGroup = ({
  options,
  label,
  groupName,
  isImportant,
  value,
  errorMessage,
  isExclusive = false,
  handleChange,
  onBlur,
  disabled,
  qapi,
  showBadges,
  shouldAskConfirmation,
  setTempValue,
  setShowConfirmationPrompt,
  depField,
  childFields,
  getError,
}: CheckBoxGroupProps) => {
  const setInputType = () => (isExclusive ? "radio" : "checkbox");

  const areValuesString = options.every(
    (element) => typeof element === "string"
  );

  function handleOnChange(fieldName: string) {
    if (handleChange) return handleChange(fieldName).onChange;
  }

  function askConfirmation(value: any) {
    setTempValue &&
      setTempValue({ fieldName: groupName, value: value, depField: depField });
    setShowConfirmationPrompt && setShowConfirmationPrompt(true);
  }

  const onChange = useCallback(
    (param: any) => {
      shouldAskConfirmation
        ? askConfirmation(param.target?.value)
        : handleOnChange(groupName)(param);
    },
    [groupName, shouldAskConfirmation]
  );

  function isOptionSelected(value: any, option: any) {
    if (isExclusive) {
      return (areValuesString ? option : option.value) === value;
    } else {
      return findStringInArray(areValuesString ? option : option.value, value);
    }
  }

  function formatLabel(label: string) {
    const valuesToFormat = ["YES", "NO"];
    if (isExclusive && valuesToFormat.includes(label)) {
      return label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
    } else {
      return label;
    }
  }

  function showOptionDepFields(option: any) {
    return !isExclusive && value && isOptionSelected(value, option);
  }

  return (
    <FormInput
      label={label}
      isImportant={isImportant}
      name={groupName}
      errorMessage={errorMessage}
      qapi={qapi}
      showBadges={showBadges}
    >
      <ul className="-mx-2 flex flex-wrap px-0">
        {options.map((option, index) => (
          <li
            key={areValuesString ? option : option.label}
            className={"mb-5 list-none px-2"}
          >
            <Box
              htmlFor={`${groupName}-${setInputType()}-${index}`}
              $shouldRenderBox={isExclusive}
            >
              <Input
                type={setInputType()}
                id={`${groupName}-${setInputType()}-${index}`}
                name={groupName}
                value={areValuesString ? option : option.value}
                checked={value && isOptionSelected(value, option)}
                onChange={onChange}
                onBlur={onBlur}
                disabled={disabled}
                data-testid={`${groupName}-${setInputType()}-${index}`}
              />
              {areValuesString
                ? formatLabel(option)
                : formatLabel(option.label)}
            </Box>
            {showOptionDepFields(option) && childFields && (
              <FormFieldGroup
                formGroup={childFields[JSON.stringify(option.toLowerCase())]}
                getError={getError}
              />
            )}
          </li>
        ))}
      </ul>
    </FormInput>
  );
};

export default CheckBoxGroup;
